import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { prepaidVouchersApi } from '../../api/services/dd_panel_prepaid_vouchers'
import { PrepaidVoucher } from '../../api/services/dd_panel_prepaid_vouchers/types.ts'
import Button from '../common/Button/Button.tsx'
import SelectInputs from '../common/forms/selectInputs/SelectInputs.tsx'
import { multiOptionsLongDiet } from '../orders/FilterSection/FilterSection.tsx'

const VoucherForm = ({
  patient_profile,
  editVoucher,
  setEditVoucher,
  formActivated,
  setFormActivated
}: {
  patient_profile: number | undefined
  editVoucher: PrepaidVoucher | null
  setEditVoucher: React.Dispatch<React.SetStateAction<PrepaidVoucher | null>>
  formActivated: boolean
  setFormActivated: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [action, setAction] = useState<'create' | 'update'>('create')
  const [selectedInputsValuesLongDiet, setSelectedInputsValuesLongDiet] =
    useState<string[]>(['1', '2', '3', '4'])

  const queryClient = useQueryClient()

  useEffect(() => {
    if (editVoucher) {
      setAction('update')
      setFormActivated(true)
    }
  }, [editVoucher, formActivated])

  const resetState = useCallback(() => {
    setSelectedInputsValuesLongDiet([])
    setEditVoucher(null)
    setAction('create')
    setFormActivated(false)
  }, [])

  const convertValuesToDietLength = useCallback((values: string[]) => {
    switch (values[0]) {
      case '1':
        return 1
      case '2':
        return 3
      case '3':
        return 6
      case '4':
        return 12
      default:
        return 1
    }
  }, [])

  const convertDietLengthToValues = useCallback((dietLength: number) => {
    switch (dietLength) {
      case 1:
        return '1'
      case 3:
        return '2'
      case 6:
        return '3'
      case 12:
        return '4'
      default:
        return '1'
    }
  }, [])

  const makeRequest = async () => {
    const dietLength = convertValuesToDietLength(selectedInputsValuesLongDiet)

    if (
      action === 'create' &&
      selectedInputsValuesLongDiet.length > 0 &&
      patient_profile !== undefined
    ) {
      await prepaidVouchersApi.createVoucher({
        diet_length: dietLength,
        patient_profile
      })
    } else if (
      action === 'update' &&
      editVoucher !== null &&
      selectedInputsValuesLongDiet.length > 0 &&
      patient_profile !== undefined
    ) {
      await prepaidVouchersApi.updateVoucher(editVoucher.id, {
        diet_length: dietLength
      })
    }
    resetState()
  }

  const { mutateAsync } = useMutation({
    mutationFn: makeRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['PrepaidVouchers'] })
    }
  })

  const onClickHandler = async () => {
    if (!formActivated) {
      setFormActivated(true)
      return
    }

    await mutateAsync()
  }

  const onCloseHandler = () => {
    resetState()
  }

  return (
    <div className="flex flex-1 flex-col md:flex-row justify-between gap-4 mt-2">
      {formActivated && (
        <div className="flex flex-1 min-w-0 items-center justify-center">
          <div className="w-full">
            <SelectInputs
              label={''}
              startWhenEmptyValue={'Ustaw długość bezpłatnej diety'}
              multiOptions={multiOptionsLongDiet}
              defaultSelected={
                editVoucher
                  ? [convertDietLengthToValues(editVoucher.diet_length)]
                  : []
              }
              selectedInputsValues={selectedInputsValuesLongDiet}
              setSelectedInputsValues={setSelectedInputsValuesLongDiet}
              canSelectMoreThanOne={false}
            />
          </div>
        </div>
      )}

      <div className={'flex items-center justify-center'}>
        {formActivated && (
          <Button
            variant="filled"
            size="medium"
            color="warning"
            type="button"
            className={'flex-1 mr-3'}
            onClick={onCloseHandler}
          >
            {'Anuluj'}
          </Button>
        )}
        <Button
          variant="filled"
          size="medium"
          color={formActivated ? 'success' : 'primary'}
          type="button"
          className={twMerge('flex-1 h-12', !formActivated && 'px-2 md:px-4')}
          onClick={onClickHandler}
          disabled={formActivated && selectedInputsValuesLongDiet.length === 0}
        >
          {formActivated
            ? 'Zapisz'
            : action === 'create'
              ? 'Utwórz bezpłatną dietę'
              : 'Edytuj'}
        </Button>
      </div>
    </div>
  )
}

export default React.memo(VoucherForm)
